import { csvDecimal } from "@hisense/shared";
import {
  MONEY_SCALE,
  type StatementDto,
  type StatementLineDto,
  type StatementReportMeta,
} from "@hisense/shared";
import type { Workbook } from "exceljs";
import { downloadWorkbook } from "./excelSheet";
import { fileStamp, type CsvColumn } from "./exportCsv";
import { newWorkbook, sheetName, writeTable, xlsxCell } from "./exportXlsx";

/**
 * Một sheet của bảng kê công nợ: khối thông tin (kỳ, phạm vi, múi giờ…) rồi
 * đến bảng. Giá trị để THÔ (kể cả csvDecimal) — bộ ghi Excel quyết định kiểu ô.
 *
 * Trước 16/09/2026 ba sheet này được tải thành BA TỆP CSV vì CSV không có
 * nhiều sheet; nay exceljs đã có trong gói nên trả lại đúng hình dạng của hệ
 * cũ: một tệp Excel, ba sheet.
 */
export interface StatementSheet {
  name: string;
  metaHeader: readonly [string, string];
  metadata: readonly (readonly unknown[])[];
  header: readonly string[];
  rows: readonly (readonly unknown[])[];
}

export const STATEMENT_BUCKET_ORDER = [
  "Current",
  "1–30",
  "31–60",
  "61–90",
  "90+",
  "Unknown",
] as const;
export const statementMoney = (value: number) =>
  (value / MONEY_SCALE).toFixed(2);
const iso = (value: number | null | undefined) =>
  value == null ? "" : new Date(value).toISOString();

/** Only positive receivables can be overdue. Age is the response snapshot's age. */
export function statementLineState(
  line: StatementLineDto,
): "archived" | "settled" | "credit" | "review" | "overdue" | "current" {
  if (line.archived) return "archived";
  if (line.balance === 0) return "settled";
  if (line.balance < 0) return "credit";
  if (line.dueBasis === "unknown" || line.dueAt == null) return "review";
  return (line.overdueDays ?? 0) > 0 ? "overdue" : "current";
}

/** Pure builder shared by downloads and regression checks. Metadata survives empty scopes. */
export function buildStatementSheets(
  groups: StatementDto[],
  meta: StatementReportMeta,
  t: (key: string) => string,
): StatementSheet[] {
  const label = (key: string) => t(`statementWorkspace.${key}`);
  const metadata: Array<{ field: string; value: unknown }> = [
    { field: label("csv_rounding"), value: label("roundingBasis") },
    {
      field: label("csv_scope"),
      value: label(meta.customerId ? "csv_customerScope" : "csv_internalScope"),
    },
    { field: label("csv_cutoff"), value: iso(meta.asOf) },
    {
      field: label("csv_start"),
      value: meta.from == null ? label("csv_allHistory") : iso(meta.from),
    },
    { field: label("csv_generated"), value: iso(meta.generatedAt) },
    { field: label("csv_mode"), value: label(`mode_${meta.mode}`) },
    { field: label("csv_filter"), value: label(`filter_${meta.filter}`) },
    { field: label("csv_search"), value: meta.q || label("csv_allDocuments") },
    {
      field: label("csv_sort"),
      value: label(`csv_sort_${meta.sort ?? "-date"}`),
    },
    {
      field: label("csv_customerId"),
      value: meta.customerId ?? label("csv_allCustomers"),
    },
    {
      field: label("csv_currency"),
      value: meta.currency ?? label("csv_separateCurrencies"),
    },
    {
      field: label("csv_timezone"),
      value: csvDecimal(meta.tzOffsetMinutes, 0),
    },
    { field: label("csv_source"), value: meta.source },
    { field: label("csv_basis"), value: label("currentBasisWarning") },
    {
      field: label("csv_history"),
      value: label(
        meta.historyLimited ? "csv_historyLimited" : "csv_historyAvailable",
      ),
    },
    {
      field: label("csv_reconciliation"),
      value: label("csv_reconciliationNote"),
    },
  ];
  const metaHeader = [label("csv_metadata"), label("csv_value")] as const;
  const sheet = <T>(
    name: string,
    columns: CsvColumn<T>[],
    rows: readonly T[],
  ): StatementSheet => ({
    name,
    metaHeader,
    metadata: metadata.map((m) => [m.field, m.value]),
    header: columns.map((c) => c.header),
    rows: rows.map((r) => columns.map((c) => c.value(r))),
  });
  const summary: CsvColumn<StatementDto>[] = [
    { header: label("csv_customerId"), value: (g) => g.customerId },
    { header: label("csv_customer"), value: (g) => g.customerName },
    { header: label("csv_currency"), value: (g) => g.currency },
    {
      header: label("csv_opening"),
      value: (g) => csvDecimal(Number(statementMoney(g.openingBalance)), 2),
    },
    {
      header: label("csv_periodBilled"),
      value: (g) => csvDecimal(Number(statementMoney(g.periodBilled)), 2),
    },
    {
      header: label("csv_periodPaid"),
      value: (g) => csvDecimal(Number(statementMoney(g.periodPaid)), 2),
    },
    {
      header: label("csv_adjustments"),
      value: (g) => csvDecimal(Number(statementMoney(g.periodAdjustments)), 2),
    },
    {
      header: label("csv_closing"),
      value: (g) => csvDecimal(Number(statementMoney(g.totalBalance)), 2),
    },
    {
      header: label("csv_receivable"),
      value: (g) => csvDecimal(Number(statementMoney(g.receivable)), 2),
    },
    {
      header: label("csv_credit"),
      value: (g) => csvDecimal(Number(statementMoney(g.credit)), 2),
    },
    {
      header: label("csv_overdue"),
      value: (g) => csvDecimal(Number(statementMoney(g.overdueAmount)), 2),
    },
    { header: label("csv_overdueCount"), value: (g) => g.overdueCount },
    {
      header: label("csv_unaged"),
      value: (g) => csvDecimal(Number(statementMoney(g.unagedAmount)), 2),
    },
    {
      header: label("csv_billed"),
      value: (g) => csvDecimal(Number(statementMoney(g.totalBilled)), 2),
    },
    {
      header: label("csv_paid"),
      value: (g) => csvDecimal(Number(statementMoney(g.totalPaid)), 2),
    },
    {
      header: label("csv_history"),
      value: (g) =>
        label(g.historyLimited ? "csv_historyLimited" : "csv_historyAvailable"),
    },
  ];
  const details = groups.flatMap((g) =>
    g.lines.map((line) => ({
      ...line,
      customerId: g.customerId,
      customerName: g.customerName,
    })),
  );
  type Detail = (typeof details)[number];
  const detailColumns: CsvColumn<Detail>[] = [
    { header: label("csv_customerId"), value: (l) => l.customerId },
    { header: label("csv_customer"), value: (l) => l.customerName },
    { header: label("csv_currency"), value: (l) => l.currency },
    { header: label("csv_piId"), value: (l) => l.piId },
    { header: label("csv_piNo"), value: (l) => l.piNo },
    { header: label("csv_date"), value: (l) => iso(l.date) },
    { header: label("csv_invoiceReference"), value: (l) => l.invoiceNo },
    { header: label("csv_invoiceArchived"), value: (l) => l.invoiceArchived },
    { header: label("csv_archived"), value: (l) => l.archived },
    {
      header: label("csv_opening"),
      value: (l) => csvDecimal(Number(statementMoney(l.openingBalance)), 2),
    },
    {
      header: label("csv_periodBilled"),
      value: (l) => csvDecimal(Number(statementMoney(l.periodBilled)), 2),
    },
    {
      header: label("csv_periodPaid"),
      value: (l) => csvDecimal(Number(statementMoney(l.periodPaid)), 2),
    },
    {
      header: label("csv_adjustments"),
      value: (l) => csvDecimal(Number(statementMoney(l.periodAdjustments)), 2),
    },
    {
      header: label("csv_closing"),
      value: (l) => csvDecimal(Number(statementMoney(l.balance)), 2),
    },
    {
      header: label("csv_receivable"),
      value: (l) => csvDecimal(Number(statementMoney(l.receivable)), 2),
    },
    {
      header: label("csv_credit"),
      value: (l) => csvDecimal(Number(statementMoney(l.credit)), 2),
    },
    {
      header: label("csv_billed"),
      value: (l) => csvDecimal(Number(statementMoney(l.total)), 2),
    },
    {
      header: label("csv_paid"),
      value: (l) => csvDecimal(Number(statementMoney(l.paid)), 2),
    },
    {
      header: label("csv_state"),
      value: (l) => label(`state_${statementLineState(l)}`),
    },
    { header: label("csv_delivery"), value: (l) => iso(l.deliveryDate) },
    { header: label("csv_terms"), value: (l) => l.paymentTerms },
    { header: label("csv_due"), value: (l) => iso(l.dueAt) },
    {
      header: label("csv_dueBasis"),
      value: (l) =>
        label(l.dueBasis === "piDelivery" ? "csv_piDelivery" : "csv_unknown"),
    },
    {
      header: label("csv_dueReason"),
      value: (l) =>
        l.dueReason == null
          ? ""
          : [
                "missingTerms",
                "unknownTerms",
                "missingDelivery",
                "statementTermsReview",
              ].includes(l.dueReason)
            ? label(`csv_reason_${l.dueReason}`)
            : l.dueReason,
    },
    { header: label("csv_age"), value: (l) => l.ageDays },
    {
      header: label("csv_overdueDays"),
      value: (l) => (statementLineState(l) === "overdue" ? l.overdueDays : ""),
    },
    {
      header: label("csv_bucket"),
      value: (l) => (l.balance > 0 ? l.bucket : ""),
    },
    { header: label("csv_historyUncertain"), value: (l) => l.historyUncertain },
  ];
  const aging = groups.flatMap((g) =>
    STATEMENT_BUCKET_ORDER.map((bucket) => {
      const match = g.buckets.find((entry) => entry.bucket === bucket);
      return {
        customerId: g.customerId,
        customerName: g.customerName,
        currency: g.currency,
        bucket,
        amount: match?.amount ?? 0,
        count: g.lines.filter(
          (line) => line.balance > 0 && line.bucket === bucket,
        ).length,
      };
    }),
  );
  return [
    sheet("summary", summary, groups),
    sheet("details", detailColumns, details),
    sheet(
      "aging",
      [
        {
          header: label("csv_customerId"),
          value: (r: (typeof aging)[number]) => r.customerId,
        },
        { header: label("csv_customer"), value: (r) => r.customerName },
        { header: label("csv_currency"), value: (r) => r.currency },
        { header: label("csv_bucket"), value: (r) => r.bucket },
        {
          header: label("csv_receivable"),
          value: (r) => csvDecimal(Number(statementMoney(r.amount)), 2),
        },
        { header: label("csv_documentCount"), value: (r) => r.count },
      ],
      aging,
    ),
  ];
}

/** Một workbook, ba sheet; mỗi sheet mở đầu bằng khối thông tin, cách một dòng, rồi bảng có tiêu đề đông cứng. */
export function buildStatementWorkbook(
  groups: StatementDto[],
  meta: StatementReportMeta,
  t: (key: string) => string,
): Workbook {
  const book = newWorkbook();
  for (const s of buildStatementSheets(groups, meta, t)) {
    const ws = book.addWorksheet(sheetName(s.name.charAt(0).toUpperCase() + s.name.slice(1)));
    s.metadata.forEach((pair, i) => {
      const row = ws.getRow(i + 1);
      const cells = pair.map(xlsxCell);
      row.values = cells.map((c) => c.value);
      cells.forEach((c, j) => {
        if (c.numFmt) row.getCell(j + 1).numFmt = c.numFmt;
      });
      row.getCell(1).font = { name: "Arial", size: 10, bold: true };
    });
    writeTable(ws, s.header, s.rows, s.metadata.length + 2);
  }
  return book;
}

export function exportStatementWorkbook(
  groups: StatementDto[],
  meta: StatementReportMeta,
  t: (key: string) => string,
): Promise<void> {
  return downloadWorkbook(
    buildStatementWorkbook(groups, meta, t),
    `statement-${meta.mode}-${meta.filter}-${fileStamp(new Date(meta.generatedAt))}`,
  );
}
